import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TemplateIntroService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { ITemplateIntro } from "../../shared/interfaces/templateIntro/ITemplateIntro";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const templateIntroSchema = yup.object().shape({
  name: yup.string().required("validation.required"),
  intro: yup.string(),
});
const TemplateIntroForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ITemplateIntro>({
    defaultValues: {
      name: "",
      intro: "",
      isDefault: false,
    },
    resolver: yupResolver(
      templateIntroSchema,
    ) as unknown as Resolver<ITemplateIntro>,
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = (templateIntro: ITemplateIntro) => {
    const contentState = editorState.getCurrentContent();
    templateIntro.intro = stateToHTML(contentState);
    const action = isEdit
      ? TemplateIntroService.update(templateIntro, Number(id))
      : TemplateIntroService.create(templateIntro);

    action
      .then(() => {
        SuccessToast(
          t(`templateIntro.${isEdit ? "successUpdate" : "successCreate"}`),
        );
        navigate("/template-intro");
      })
      .catch(ErrorToast);
  };
  useEffect(() => {
    if (id) {
      const fetchTemplateIntro = async () => {
        try {
          const response = await TemplateIntroService.getSingle(+id!);
          reset(response);
          const blocksFromHTML = convertFromHTML(response?.intro || "");
          const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap,
          );
          setEditorState(EditorState.createWithContent(contentState));
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchTemplateIntro();
    }
  }, [id]);
  return (
    <Card>
      <CardBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            <Col sm={12} xl={5}>
              <Form.Group>
                <Form.Label className={"required"}>
                  {t("general.name")} / {t("general.company")}
                </Form.Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Control
                      type="text"
                      isInvalid={!!errors?.name?.message?.length}
                      placeholder={t("general.enterName")}
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name && t("validation.required")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="intro">
                <Form.Label>{t("general.intro")}</Form.Label>
                <Controller
                  name="intro"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(editorState) => {
                        setEditorState(editorState);
                        field.onChange(
                          stateToHTML(editorState.getCurrentContent()),
                        );
                      }}
                      wrapperClassName="demo-wrapper"
                      editorClassName={`form-control ${errors.intro ? "is-invalid" : ""}`}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {t(errors.intro?.message as string)}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} xl={5}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label={t("templateIntro.isDefault")}
                >
                  <Controller
                    name="isDefault"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Form.Check.Input
                        type="checkbox"
                        checked={value || false}
                        onChange={onChange}
                      />
                    )}
                  />
                  <Form.Check.Label>{t("templateIntro.isDefault")}</Form.Check.Label>
                </Form.Check>
              </Form.Group>
            </Col>
            <Col sm={12} className="d-flex gap-2">
              <Button type="submit" variant="primary">
                {t(`templateIntro.${isEdit ? "update" : "create"}`)}
              </Button>
              <Button type="button" variant="secondary" onClick={() => guardedNavigate("/template-intro")}>
                {t("button.cancel")}
              </Button>
            </Col>
          </Row>
        </Form>
        {unsavedChangesModal}
      </CardBody>
    </Card>
  );
};

export default TemplateIntroForm;
